import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const services = [
  {
    title: 'Décoration Événementielle',
    icon: 'ri-star-smile-line',
    path: '/decoration-evenementielle',
    color: 'fuchsia',
    image: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/e13c309307a2dbc91d946f57882e62b7.jpeg',
    items: [
      'Accompagnement et conseil',
      'Création d\'ambiances sur mesure',
      'Mise en place complète',
    ],
  },
  {
    title: "Décoration d'Intérieur",
    icon: 'ri-home-heart-line',
    path: '/decoration-interieur',
    color: 'coral',
    image: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/eb326ba8bb099947f664ff390dc64d94.jpeg',
    items: [
      'Conception 3D',
      'Conseil en aménagement',
      'Mobilier sur mesure',
    ],
  },
  {
    title: 'Confection 3D',
    icon: 'ri-box-3-line',
    path: '/creation-3d',
    color: 'gold',
    image: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/5c1a17ba0dc0e7ed61fb2097b301a169.jpeg',
    items: [
      'Objets personnalisés',
      'Figurines',
      'Goodies',
    ],
  },
];

const colorMap: Record<string, { icon: string; dot: string }> = {
  fuchsia: { icon: 'bg-brand-fuchsia', dot: 'bg-brand-fuchsia' },
  coral: { icon: 'bg-brand-coral', dot: 'bg-brand-coral' },
  gold: { icon: 'bg-brand-gold', dot: 'bg-brand-gold' },
};

const ServicesSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="nos-services" className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-brand-coral" />
            <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">Ce que nous faisons</span>
            <span className="h-px w-6 bg-brand-coral" />
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite mb-4">Nos Services</h2>
          <p className="text-brand-gray max-w-xl mx-auto text-base leading-relaxed">
            De la décoration événementielle à la création 3D, nous mettons notre expertise au service de vos projets les plus ambitieux.
          </p>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <div
              key={service.title}
              className={`group bg-brand-beige rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 150}ms`, transitionDuration: '600ms' }}
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Icon + Title */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${colorMap[service.color].icon}`}>
                    <i className={`${service.icon} text-white text-lg`} />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-brand-anthracite">{service.title}</h3>
                </div>

                {/* Divider */}
                <div className="h-px w-full bg-brand-gold/30 mb-4" />

                {/* Items */}
                <ul className="space-y-2 mb-6">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-brand-gray">
                      <div className={`w-1.5 h-1.5 flex items-center justify-center rounded-full flex-shrink-0 ${colorMap[service.color].dot}`} />
                      {item}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={service.path}
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand-fuchsia hover:text-brand-coral transition-colors group-hover:gap-2"
                >
                  En savoir plus
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
